import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Container, Section, SectionHeader, Kicker, PullQuote } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { ExplainerVideo } from '../components/sections/ExplainerVideo';
import { METHOD_STEPS } from '../data/services';
export function Method() {
  const [active, setActive] = React.useState(0);
  const reduced = useReducedMotion();
  const step = METHOD_STEPS[active];
  return <>
      <section className="surface-dark" data-id="element-1117" data-mp="3lgrpwiygtofb">
        <Container className="py-16 sm:py-24" data-id="element-1118" data-mp="3lhsz0hvx80ue">
          <Kicker data-id="element-1119" data-mp="37p3e6gs4p1yv">Augmented intelligence</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-tight text-white sm:text-[46px]" data-id="element-1120" data-mp="37qehkfhnw7pg">
            Made for people, powered by AI
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-1121" data-mp="2xqdsse10wzdv">
            Augmented intelligence is not automation with better marketing. It is the deliberate pairing of
            machine analysis with human judgement, arranged so that a person remains accountable for the decision.
            Four steps carry that principle into every engagement.
          </p>
        </Container>
      </section>

      <Section aria-labelledby="stepper" data-id="element-1122" data-mp="ni8z12v22qop">
        <SectionHeader id="stepper" kicker="The method" title="Capture, apply, enable, decide" lead="Move through each step to see what it involves and what you receive." data-id="element-1123" data-mp="nfwld1wbz2hg" />

        <div className="mt-12" data-id="element-1124" data-mp="3mx4en8a8ware">
          <ol role="tablist" aria-label="Method steps" className="grid gap-px overflow-hidden rounded-md bg-line sm:grid-cols-4" data-id="element-1125" data-mp="3my5o58bm4p70">
            {METHOD_STEPS.map((item, index) => {
            const isActive = index === active;
            return <li key={item.id} data-id="element-1126" data-mp="3vvzhqbhzt4qh" data-mpi={index}>
                  <button type="button" role="tab" id={`step-tab-${item.id}`} aria-selected={isActive} aria-controls="step-panel" onClick={() => setActive(index)} className={`relative w-full px-5 py-6 text-left transition-colors ${isActive ? 'bg-gold text-charcoal' : 'bg-panel text-body hover:bg-mist'}`} data-id="element-1127" data-mp="387gbe1ii7211">
                    <span className={`font-display text-[13px] ${isActive ? 'text-gold' : 'text-muted'}`} data-id="element-1128" data-mp="1ltv4rx2o2poi">
                      0{index + 1}
                    </span>
                    <span className={`mt-2 block font-display text-[21px] ${isActive ? 'text-white' : 'text-ink'}`} data-id="element-1129" data-mp="t5z8gth0ezs9">
                      {item.title}
                    </span>
                  </button>
                </li>;
          })}
          </ol>

          <motion.div key={step.id} id="step-panel" role="tabpanel" aria-labelledby={`step-tab-${step.id}`} initial={reduced ? false : {
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="mt-8 grid gap-8 rounded-md bg-mist p-7 sm:p-10 lg:grid-cols-[1.4fr_0.6fr]" data-id="element-1130" data-mp="124t3ifeozi3p">
            <div data-id="element-1131" data-mp="3pbjlehl3o8lg">
              <h3 className="font-display text-[26px] leading-tight text-ink sm:text-[30px]" data-id="element-1132" data-mp="3pb00qt42f63q">{step.summary}</h3>
              <p className="mt-5 max-w-prose text-[16px] text-body" data-id="element-1133" data-mp="3kbnd465ddv03">{step.detail}</p>
            </div>
            <div className="border-l-2 border-gold pl-5" data-id="element-1134" data-mp="3feewi9zvdz0y">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-1135" data-mp="3aesja0k1kis5">What you receive</p>
              <p className="mt-3 font-display text-[20px] leading-snug text-ink" data-id="element-1136" data-mp="36cfdxd97imvw">{step.output}</p>
            </div>
          </motion.div>
        </div>
      </Section>

      <Section tone="mist" aria-labelledby="explainer" data-id="element-1137" data-mp="31g75eipilrxp">
        <SectionHeader id="explainer" kicker="Watch" title="The method in motion" lead="The same four steps, animated end to end, from scattered evidence to a decision somebody owns." data-id="element-1138" data-mp="14k9csh5wp7vy" />
        <div className="mt-10" data-id="element-1139" data-mp="3sev7r5jbyd2b">
          <ExplainerVideo data-id="element-1140" data-mp="1b6lnu3jiu38" />
        </div>
      </Section>

      <Section tone="champagne" data-id="element-1141" data-mp="19vfxoqjbc9h">
        <div className="grid gap-12 lg:grid-cols-2" data-id="element-1142" data-mp="3jgjl1ell3zf5">
          <div data-id="element-1143" data-mp="3jfrtcev3ylu5">
            <Kicker data-id="element-1144" data-mp="3jidw81720d61">The ethos</Kicker>
            <h2 className="mt-4 font-display text-[28px] leading-tight text-ink sm:text-[34px]" data-id="element-1145" data-mp="3oeuu3n4225wb">
              A person stays accountable for the decision
            </h2>
            <p className="mt-5 text-[16px] text-body" data-id="element-1146" data-mp="39ondze1isyys">
              Every system we build has a named reviewer, a documented route of appeal and a record of what the
              machine contributed. This is not a compliance gesture. It is what keeps a tool useful once the
              novelty passes and someone has to answer for an outcome.
            </p>
            <div className="mt-8" data-id="element-1147" data-mp="2qs4ap5dee5a5">
              <Button to="/assessment" data-id="element-1148" data-mp="2vqpax4sngoyl">Test your readiness</Button>
            </div>
          </div>
          <PullQuote data-id="element-1149" data-mp="2c4x6rjmby7kr">
            We would rather hand over a smaller system that your people trust than a larger one they quietly stop
            using.
          </PullQuote>
        </div>
      </Section>
    </>;
}